import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircle2, Clock, XCircle } from 'lucide-react'
import { Button, Loader } from '@/components/atoms'
import { authService } from '@/services/authService'
import {
  EmailDomainVerificationError,
  organizationService,
} from '@/services/organizationService'
import { EmailDomainVerification, EmailDomainVerificationFailure } from '@/types/organization'

/**
 * Landing page for the link in an organization domain-verification email:
 * `/organizations/verify-domain/:token`.
 *
 * PUBLIC by design. The challenge is mailed to an address at the *claimed*
 * domain — usually an IT or ops contact who has no SecondLane account at all —
 * so this screen renders its own chrome-less shell rather than sitting inside
 * the app layout, and never assumes a session exists.
 */

type VerifyState =
  | { kind: 'verifying' }
  | { kind: 'success'; result: EmailDomainVerification }
  | { kind: 'failure'; reason: EmailDomainVerificationFailure; message: string }

const CARD_SHELL =
  'border border-white bg-[rgba(255,255,255,0.32)] dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]'

const HEADING =
  'font-plus-jakarta font-medium text-[24px] leading-[32px] text-[#2d2923] dark:text-white'

const BODY =
  'font-plus-jakarta font-normal text-[16px] leading-[24px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]'

/** Copy for each failure mode. `invalid` deliberately covers "unknown OR already used". */
const FAILURE_COPY: Record<
  EmailDomainVerificationFailure,
  { title: string; body: string }
> = {
  expired: {
    title: 'This link has expired',
    body:
      'Domain verification links are only valid for a short time. Ask an administrator of the organization to send a new verification email, then open the newest link.',
  },
  invalid: {
    title: 'This link is no longer valid',
    body:
      'It may have already been used, or it may have been copied incompletely from the email. If the domain still shows as unverified, ask an administrator of the organization to send a new verification email.',
  },
  error: {
    title: 'We could not verify the domain',
    body: 'Something went wrong on our side. Please try the link again in a few minutes.',
  },
}

const VerifyDomainScreen = () => {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<VerifyState>({ kind: 'verifying' })

  // Redemption is single-use and destructive: the second POST of the same token
  // gets a 404 and would show "already used" on a link that just worked. React
  // StrictMode double-invokes effects in development, so the token that has
  // already been sent is recorded on a ref, which survives that remount.
  const redeemedTokenRef = useRef<string | null>(null)
  // Kept separate from the guard above: a StrictMode remount tears the effect
  // down and back up, so an `active` flag scoped to the *effect* would be false
  // by the time the one in-flight request resolves, and the result would be
  // dropped forever.
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (redeemedTokenRef.current === token) return
    redeemedTokenRef.current = token

    setState({ kind: 'verifying' })
    organizationService
      .verifyEmailDomain(token)
      .then(result => {
        if (!mountedRef.current) return
        setState({ kind: 'success', result })
      })
      .catch(err => {
        if (!mountedRef.current) return
        const reason: EmailDomainVerificationFailure =
          err instanceof EmailDomainVerificationError ? err.reason : 'error'
        setState({ kind: 'failure', reason, message: FAILURE_COPY[reason].body })
      })
  }, [token])

  const isAuthenticated = authService.isAuthenticated()

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className={`w-full max-w-[520px] px-6 py-10 md:px-10 text-center ${CARD_SHELL}`}>
        {state.kind === 'verifying' && (
          <>
            <Loader size="lg" className="text-[#2d2923] dark:text-white mb-4" />
            <h1 className={`${HEADING} mb-2`}>Verifying your domain</h1>
            <p className={BODY}>One moment while we confirm this verification link.</p>
          </>
        )}

        {state.kind === 'success' && (
          <>
            <CheckCircle2 size={40} className="text-[#1fb845] mx-auto mb-4" />
            <h1 className={`${HEADING} mb-2`}>Domain verified</h1>
            <p className={`${BODY} mb-8`}>
              {state.result.email_domain ? (
                <>
                  <span className="font-medium text-[#2d2923] dark:text-white">
                    {state.result.email_domain}
                  </span>{' '}
                  is now verified for{' '}
                </>
              ) : (
                'This domain is now verified for '
              )}
              <span className="font-medium text-[#2d2923] dark:text-white">
                {state.result.organization_name}
              </span>
              . No further action is needed — you can close this page.
            </p>
            {isAuthenticated ? (
              <Button variant="primary" onClick={() => navigate('/organizations')}>
                Go to organizations
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate('/login')}>
                Sign in to SecondLane
              </Button>
            )}
          </>
        )}

        {state.kind === 'failure' && (
          <>
            {state.reason === 'expired' ? (
              <Clock size={40} className="text-[#ff540c] mx-auto mb-4" />
            ) : (
              <XCircle size={40} className="text-[#ff540c] mx-auto mb-4" />
            )}
            <h1 className={`${HEADING} mb-2`}>{FAILURE_COPY[state.reason].title}</h1>
            <p className={`${BODY} mb-8`}>{state.message}</p>
            {isAuthenticated ? (
              <Button variant="outline" onClick={() => navigate('/organizations')}>
                Go to organizations
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate('/login')}>
                Sign in to SecondLane
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default VerifyDomainScreen
